/**
 * The weapon side of `.pxm` — version 3's third resolver, alongside
 * `specFor` (rigid masks) and `tileMask.ts`'s `kindGrid` (room units).
 *
 * ==== WHAT A WEAPON DOCUMENT IS ==========================================
 *
 * The blade is the same **material grid** a rigid mask already draws — a
 * small pixel shape, stamped along the swing arc where a procedural capsule
 * is stamped today (`weaponVisual.ts`; wiring the render path onto it is
 * issue #168 phase 2's own work, not this file's). `weaponSpecFor` refuses a
 * deferred class the way `specFor` does: a blade is carried by whoever
 * wields it, not by a host biome, so it has nothing to defer to either.
 *
 * The feel/swing declarations (`feel`, `node` — `docs/29-pxm-format.md`
 * §4.5) carry the two things a grid cannot say about a weapon: how heavy it
 * feels, and how its swing is timed and drawn. This is deliberately **not**
 * the whole of a `WeaponDef` — damage, poise, primers, hitbox shapes, slots,
 * scaling and tags stay combat-balance decisions in `weaponData.ts`'s code
 * literals, per issue #168's own design. A `node` line only ever adds
 * timing/arc on top of whatever combat-balance override the code literal
 * already carries for that key; it never replaces one.
 */

import { deferredClassOf, VOID_CHAR, WEAPON_NODE_KEYS, type ShapeMaskDoc, type WeaponNodeKey } from '../sim/shapeMask';
import { VOID, type MatId } from '../sim/materials';
import type { ComboArc, EaseKind } from './types';

class WeaponMaskError extends Error {}

/** A blade's own shape, resolved to real material ids. */
export interface WeaponBladeSpec {
  readonly w: number;
  readonly h: number;
  /** Row-major, length `w * h`. `VOID` where the grid is empty. */
  readonly cells: readonly MatId[];
}

/** One combo key's resolved timing/arc override — everything a `node` line can set. */
export interface WeaponNodeOverride {
  readonly w?: number;
  readonly a?: number;
  readonly r?: number;
  readonly wm?: number;
  readonly am?: number;
  readonly rm?: number;
  readonly arc?: ComboArc;
}

/** A whole document, resolved — what `weaponData.ts` merges into a weapon `Spec`. */
export interface WeaponSpec {
  readonly blade: WeaponBladeSpec;
  readonly weight: number;
  readonly nodes: ReadonlyMap<WeaponNodeKey, WeaponNodeOverride>;
}

/**
 * Resolve a `.pxm` document as a weapon — **gate 3**.
 *
 * `resolve` is passed in for `specFor`'s own reason: this file then holds no
 * copy of the material table, and a test can hand it a resolver that does
 * not know a name that exists in the shipping build.
 *
 * **`doc.feel` is required.** A document with no `feel` line has no weight,
 * and a weapon with no weight is not a weapon — the same refusal `specFor`
 * gives a document with a deferred class in it: the vocabulary is legal
 * `.pxm`, just not legal *for this resolver*.
 */
export function weaponSpecFor(doc: ShapeMaskDoc, resolve: (name: string) => MatId | undefined): WeaponSpec {
  if (doc.feel === undefined) {
    throw new WeaponMaskError(`shape ${doc.name}: no \`feel\` line — a weapon document authors its own weight`);
  }

  const byChar = new Map<string, MatId>();
  for (const [ch, matName] of doc.legend) {
    if (deferredClassOf(matName) !== undefined) {
      throw new WeaponMaskError(
        `shape ${doc.name}: \`${matName}\` is a deferred class, and a blade has no host to defer to`,
      );
    }
    const id = resolve(matName);
    if (id === undefined) throw new WeaponMaskError(`shape ${doc.name}: unknown material ${matName}`);
    byChar.set(ch, id);
  }
  const cells: MatId[] = new Array(doc.w * doc.h);
  for (let y = 0; y < doc.h; y++) {
    for (let x = 0; x < doc.w; x++) {
      const ch = doc.rows[y][x];
      cells[y * doc.w + x] = ch === VOID_CHAR ? VOID : (byChar.get(ch) as MatId);
    }
  }

  const nodes = new Map<WeaponNodeKey, WeaponNodeOverride>();
  for (const n of doc.nodes) {
    const arcFields = [n.arcStart, n.arcEnd, n.ease];
    const arcTouched = arcFields.some((f) => f !== undefined);
    let arc: ComboArc | undefined;
    if (arcTouched) {
      if (n.arcStart === undefined || n.arcEnd === undefined || n.ease === undefined) {
        throw new WeaponMaskError(
          `shape ${doc.name}: node ${n.key}: an arc is \`arcStart\`, \`arcEnd\` and \`ease\` together or not at all`,
        );
      }
      arc = { start: n.arcStart, end: n.arcEnd, ease: n.ease as EaseKind };
    }
    nodes.set(n.key, { w: n.w, a: n.a, r: n.r, wm: n.wm, am: n.am, rm: n.rm, arc });
  }

  return { blade: { w: doc.w, h: doc.h, cells }, weight: doc.feel.weight, nodes };
}

/** The seven node keys, re-exported for callers that would otherwise import `shapeMask.ts` just for this list. */
export { WEAPON_NODE_KEYS };
export type { WeaponNodeKey };
